import { useEffect, useState } from "react";
import { AppleIcon, WindowsIcon, LinuxIcon } from "./PlatformIcons.js";
import release from "../lib/desktop-release.json";

/**
 * Desktop download cluster for the hero — three platform buttons that
 * download the actual installer in one click (direct asset URLs resolved
 * at build time by scripts/fetch-desktop-release.mjs). The visitor's own
 * OS is detected and floated to the front as the primary button.
 *
 * Platforms shipped today: macOS (Apple Silicon .dmg), Windows (.exe),
 * Linux (Debian/Ubuntu .deb). No Intel-Mac / AppImage / rpm builds yet,
 * so we don't advertise them.
 */
type OS = "mac" | "windows" | "linux";

type Platform = {
  os: OS;
  label: string;
  Icon: typeof AppleIcon;
  href: string | null;
  note: string;
};

const PLATFORMS: Platform[] = [
  { os: "mac", label: "macOS", Icon: AppleIcon, href: release.downloads.macArm64, note: "Apple Silicon · .dmg" },
  { os: "windows", label: "Windows", Icon: WindowsIcon, href: release.downloads.windows, note: "x64 installer · .exe" },
  { os: "linux", label: "Linux", Icon: LinuxIcon, href: release.downloads.linuxDeb, note: "Debian / Ubuntu · .deb" },
];

function detectOS(): OS | null {
  if (typeof navigator === "undefined") return null;
  const ua = `${navigator.userAgent} ${navigator.platform}`.toLowerCase();
  if (/mac|iphone|ipad|ipod/.test(ua)) return "mac";
  if (/win/.test(ua)) return "windows";
  if (/linux|x11|cros/.test(ua)) return "linux";
  return null;
}

export function DesktopDownload() {
  // Resolve on mount so the markup is stable before hydration and the
  // detected platform just gets promoted client-side.
  const [detected, setDetected] = useState<OS | null>(null);
  useEffect(() => setDetected(detectOS()), []);

  // Detected OS first (so it's the primary, leftmost button), rest follow
  // in their canonical order.
  const ordered = detected
    ? [...PLATFORMS].sort((a, b) => (a.os === detected ? -1 : b.os === detected ? 1 : 0))
    : PLATFORMS;

  return (
    <div className="ax-dl">
      <div className="ax-dl-label">Or run the desktop app</div>

      <div className="ax-dl-btns">
        {ordered.map(({ os, label, Icon, href, note }) => {
          const isPrimary = detected === os;
          return (
            <a
              key={os}
              href={href ?? release.releaseUrl}
              // `download` asks the browser to save rather than navigate;
              // GitHub serves the asset with attachment headers anyway.
              download={!!href}
              target={href ? undefined : "_blank"}
              rel="noreferrer"
              className={"ax-dl-btn" + (isPrimary ? " primary" : "")}
              title={`${label} — ${note}`}
              aria-label={`Download Axon for ${label} (${note})`}
            >
              <Icon size={17} />
              <span className="ax-dl-btn-os">{label}</span>
            </a>
          );
        })}
      </div>

      <div className="ax-dl-meta">
        <span>v{release.version}</span>
        <span className="ax-dl-dot" aria-hidden>·</span>
        <a href={release.releaseUrl} target="_blank" rel="noreferrer" className="ax-dl-all">
          all platforms ↗
        </a>
      </div>

      <style>{`
        .ax-dl {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 14px;
        }
        .ax-dl-label {
          font-family: var(--at-font-mono);
          font-size: 11px;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: var(--at-muted);
        }
        .ax-dl-btns {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 10px;
        }
        .ax-dl-btn {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          padding: 11px 18px;
          border-radius: 10px;
          border: 1px solid var(--at-hairline);
          background: var(--at-surface);
          color: var(--at-fg-dim);
          font-size: 14px;
          font-weight: 500;
          transition: border-color var(--at-dur-fast) var(--at-ease),
                      color var(--at-dur-fast) var(--at-ease),
                      background var(--at-dur-fast) var(--at-ease),
                      transform var(--at-dur-fast) var(--at-ease);
        }
        .ax-dl-btn:hover {
          color: var(--at-fg);
          border-color: var(--at-hairline-strong);
          transform: translateY(-1px);
        }
        .ax-dl-btn .ax-dl-btn-os { line-height: 1; }
        /* The visitor's detected OS — bright, leads the row. */
        .ax-dl-btn.primary {
          background: rgba(74,222,128,0.10);
          border-color: rgba(74,222,128,0.45);
          color: var(--at-accent);
        }
        .ax-dl-btn.primary:hover {
          background: rgba(74,222,128,0.16);
          border-color: rgba(74,222,128,0.65);
          color: var(--at-accent);
        }
        .ax-dl-meta {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-family: var(--at-font-mono);
          font-size: 11px;
          color: var(--at-muted);
        }
        .ax-dl-all {
          color: var(--at-fg-dim);
          transition: color var(--at-dur-fast) var(--at-ease);
        }
        .ax-dl-all:hover { color: var(--at-fg); }
      `}</style>
    </div>
  );
}
